<template>
  <div class="row">
    <div class="col-md-12">
      <div class="category-top-bar">
        <div class="top-bar">
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="detail ? closeDetail() : goBack()">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </div>
      </div>
      <p class="clearfix"></p>

      <!-- ================= LIST ================= -->
      <div v-show="!detail" class="card">
        <div class="card-content">
          <h4 class="msubs-title">{{ $t('menuSubscribers.title') }}</h4>

          <div class="msubs-filters">
            <div class="msubs-filter">
              <label class="msubs-label">{{ $t('menuSubscribers.offer') }}</label>
              <el-select v-model="filters.menu_subscription_id" clearable filterable :placeholder="$t('menuSubscribers.allOffers')" @change="reload">
                <el-option v-for="offer in offers" :key="offer.id" :label="offer.name" :value="offer.id"></el-option>
              </el-select>
            </div>
            <div class="msubs-filter msubs-filter--search">
              <label class="msubs-label">{{ $t('menuSubscribers.customer') }}</label>
              <input type="text" class="form-control" v-model="filters.search" @input="onSearchInput" :placeholder="$t('menuSubscribers.searchPlaceholder')">
            </div>
          </div>

          <div class="msubs-chips">
            <a v-for="chip in chips" :key="chip"
               class="msubs-chip" :class="['msubs-chip--' + chip, { 'is-active': filters.status === chip }]"
               @click="setStatus(chip)">
              {{ $t('menuSubscribers.status.' + chip) }} <span class="msubs-chip__count">{{ counts[chip] || 0 }}</span>
            </a>
          </div>

          <el-table :data="items" v-loading="loading" style="width: 100%" class="msubs-table"
                    :empty-text="$t('menuSubscribers.empty')" @row-click="openDetail">
            <el-table-column :label="$t('menuSubscribers.customer')" min-width="190">
              <template slot-scope="props">
                <strong>{{ props.row.customer.name || ('#' + props.row.customer.id) }}</strong>
                <div class="msubs-muted">{{ props.row.customer.phone }}<span v-if="props.row.customer.email"> · {{ props.row.customer.email }}</span></div>
              </template>
            </el-table-column>
            <el-table-column v-if="!filters.menu_subscription_id" :label="$t('menuSubscribers.offer')" min-width="140">
              <template slot-scope="props">{{ props.row.menu_subscription.name }}</template>
            </el-table-column>
            <el-table-column :label="$t('menuSubscribers.period')" min-width="150">
              <template slot-scope="props">
                {{ formatDate(props.row.start_date) }} - {{ formatDate(props.row.end_date) }}
                <div class="msubs-muted">{{ props.row.delivery_from }}-{{ props.row.delivery_to }} · {{ $t('menuSubscribers.portions', { count: props.row.quantity }) }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('menuSubscribers.deliveries')" min-width="150">
              <template slot-scope="props">
                <span :title="$t('menuSubscribers.deliveriesHint')">
                  <strong>{{ props.row.deliveries.delivered }}</strong> / {{ props.row.deliveries.ordered }} / {{ props.row.deliveries.total }}
                </span>
                <div class="msubs-muted">
                  <span v-if="props.row.deliveries.scheduled">{{ $t('menuSubscribers.scheduledCount', { count: props.row.deliveries.scheduled }) }}</span>
                  <span v-if="props.row.deliveries.cancelled"> · {{ $t('menuSubscribers.cancelledCount', { count: props.row.deliveries.cancelled }) }}</span>
                  <span v-if="props.row.deliveries.errors" class="text-danger"> · {{ $t('menuSubscribers.errorsCount', { count: props.row.deliveries.errors }) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('menuSubscribers.money')" min-width="150">
              <template slot-scope="props">
                {{ props.row.total }} {{ currency }}
                <div class="msubs-muted">
                  {{ $t('menuSubscribers.paid') }}: {{ props.row.total_paid }}
                  <span v-if="parseFloat(props.row.refunded_amount) > 0" class="text-danger"> · {{ $t('menuSubscribers.refunded') }}: {{ props.row.refunded_amount }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('menuSubscribers.statusLabel')" min-width="130">
              <template slot-scope="props">
                <span class="msubs-state" :class="'msubs-state--' + props.row.status">{{ $t('menuSubscribers.status.' + props.row.status) }}</span>
                <div class="msubs-muted">{{ formatDateTime(props.row.paid_at || props.row.created_at) }}</div>
              </template>
            </el-table-column>
            <el-table-column width="100" align="center">
              <template slot-scope="props">
                <a class="msubs-link" @click.stop="openDetail(props.row)">{{ $t('menuSubscribers.details') }} <i class="fa fa-angle-right"></i></a>
              </template>
            </el-table-column>
          </el-table>

          <el-pagination
            v-if="pagination.last_page > 1"
            class="msubs-pagination"
            layout="prev, pager, next"
            :current-page="pagination.current_page"
            :page-size="pagination.per_page"
            :total="pagination.total"
            @current-change="changePage">
          </el-pagination>
        </div>
      </div>

      <!-- ================= DETAIL ================= -->
      <div v-if="detail" class="card" v-loading="detailLoading">
        <div class="card-content">
          <div class="msubs-detail__head">
            <div>
              <h4 class="msubs-title">
                {{ detail.customer.name || ('#' + detail.customer.id) }}
                <span class="msubs-state" :class="'msubs-state--' + detail.status">{{ $t('menuSubscribers.status.' + detail.status) }}</span>
              </h4>
              <div class="msubs-muted">
                #{{ detail.id }} · {{ detail.menu_subscription.name }} · {{ detail.customer.phone }}<span v-if="detail.customer.email"> · {{ detail.customer.email }}</span>
              </div>
            </div>
            <div class="msubs-actions">
              <button v-if="detail.can_stop" class="btn btn-sm btn-danger" @click="toggleBox('stop')">
                <i class="fa fa-stop-circle"></i> {{ $t('menuSubscribers.stop') }}
              </button>
              <button v-if="detail.can_refund" class="btn btn-sm btn-warning" @click="toggleBox('refund')">
                <i class="fa fa-undo"></i> {{ $t('menuSubscribers.refund') }}
              </button>
            </div>
          </div>

          <!-- stop -->
          <div v-if="box === 'stop'" class="msubs-box msubs-box--stop">
            <p>{{ $t('menuSubscribers.stopHint', { count: detail.summary.deliveries.scheduled + detail.summary.deliveries.errors }) }}</p>
            <label>{{ $t('menuSubscribers.reason') }}*</label>
            <textarea class="form-control" rows="2" v-model="stopReason" :placeholder="$t('menuSubscribers.reasonPlaceholder')"></textarea>
            <div class="msubs-box__buttons">
              <button class="btn btn-sm btn-default" @click="box = null">{{ $t('general.cancel') }}</button>
              <button class="btn btn-sm btn-danger btn-fill" :disabled="busy || !stopReason.trim()" @click="stop">{{ $t('menuSubscribers.stopConfirm') }}</button>
            </div>
          </div>

          <!-- refund -->
          <div v-if="box === 'refund'" class="msubs-box msubs-box--refund">
            <p>
              {{ $t('menuSubscribers.refundHint', { max: money(detail.summary.refundable), notDelivered: money(detail.summary.value_not_delivered) }) }}
            </p>
            <div class="row">
              <div class="col-md-3">
                <label>{{ $t('menuSubscribers.amount') }}* <small class="text-muted">({{ currency }})</small></label>
                <input type="number" min="0.01" step="0.01" :max="detail.summary.refundable" class="form-control" v-model="refundAmount">
                <a class="msubs-link msubs-small" @click="refundAmount = money(detail.summary.suggested_refund)">{{ $t('menuSubscribers.useSuggested', { amount: money(detail.summary.suggested_refund) }) }}</a>
              </div>
              <div class="col-md-9">
                <label>{{ $t('menuSubscribers.reason') }}*</label>
                <textarea class="form-control" rows="2" v-model="refundReason" :placeholder="$t('menuSubscribers.reasonPlaceholder')"></textarea>
              </div>
            </div>
            <small class="text-muted msubs-block">{{ $t('menuSubscribers.refundProcessorHint') }}</small>
            <div class="msubs-box__buttons">
              <button class="btn btn-sm btn-default" @click="box = null">{{ $t('general.cancel') }}</button>
              <button class="btn btn-sm btn-warning btn-fill" :disabled="busy || !validRefund" @click="refund">{{ $t('menuSubscribers.refundConfirm') }}</button>
            </div>
          </div>

          <!-- money + deliveries summary -->
          <div class="msubs-cards">
            <div class="msubs-card"><span>{{ $t('menuSubscribers.total') }}</span><strong>{{ detail.total }} {{ currency }}</strong></div>
            <div class="msubs-card"><span>{{ $t('menuSubscribers.paid') }}</span><strong>{{ detail.total_paid }} {{ currency }}</strong></div>
            <div class="msubs-card"><span>{{ $t('menuSubscribers.refunded') }}</span><strong>{{ detail.refunded_amount }} {{ currency }}</strong></div>
            <div class="msubs-card"><span>{{ $t('menuSubscribers.refundable') }}</span><strong>{{ money(detail.summary.refundable) }} {{ currency }}</strong></div>
            <div class="msubs-card msubs-card--ok"><span>{{ $t('menuSubscribers.valueDelivered') }}</span><strong>{{ money(detail.summary.value_delivered) }} {{ currency }}</strong></div>
            <div class="msubs-card msubs-card--warn"><span>{{ $t('menuSubscribers.valueNotDelivered') }}</span><strong>{{ money(detail.summary.value_not_delivered) }} {{ currency }}</strong></div>
          </div>
          <p class="msubs-deliveries-line">
            <strong>{{ $t('menuSubscribers.deliveredOf', { delivered: detail.summary.deliveries.delivered, total: detail.summary.deliveries.total }) }}</strong>
            · {{ $t('menuSubscribers.orderedCount', { count: detail.summary.deliveries.ordered }) }}
            · {{ $t('menuSubscribers.scheduledCount', { count: detail.summary.deliveries.scheduled }) }}
            <span v-if="detail.summary.deliveries.cancelled"> · {{ $t('menuSubscribers.cancelledCount', { count: detail.summary.deliveries.cancelled }) }}</span>
            <span v-if="detail.summary.deliveries.errors" class="text-danger"> · {{ $t('menuSubscribers.errorsCount', { count: detail.summary.deliveries.errors }) }}</span>
          </p>

          <!-- info -->
          <div class="row msubs-info">
            <div class="col-md-6">
              <div><span class="msubs-muted">{{ $t('menuSubscribers.period') }}:</span> {{ formatDate(detail.start_date) }} - {{ formatDate(detail.end_date) }}</div>
              <div><span class="msubs-muted">{{ $t('menuSubscribers.interval') }}:</span> {{ detail.delivery_from }}-{{ detail.delivery_to }}</div>
              <div><span class="msubs-muted">{{ $t('menuSubscribers.portionsLabel') }}:</span> {{ detail.quantity }} · {{ detail.price_per_day }} {{ currency }}/{{ $t('menuSubscribers.day') }} · {{ $t('menuSubscribers.deliveryFee') }} {{ detail.delivery_fee }} {{ currency }}</div>
              <div><span class="msubs-muted">{{ $t('menuSubscribers.source') }}:</span> {{ detail.source === 'website' ? 'Website' : 'App' }}</div>
            </div>
            <div class="col-md-6">
              <div><span class="msubs-muted">{{ $t('menuSubscribers.address') }}:</span> {{ addressText(detail.address) }}</div>
              <div v-if="detail.zone_name"><span class="msubs-muted">{{ $t('menuSubscribers.zone') }}:</span> {{ detail.zone_name }}</div>
              <div><span class="msubs-muted">{{ $t('menuSubscribers.phone') }}:</span> {{ detail.contact_phone }}</div>
              <div v-if="detail.comments"><span class="msubs-muted">{{ $t('menuSubscribers.comments') }}:</span> {{ detail.comments }}</div>
              <div v-if="detail.stopped_at" class="text-danger">
                {{ $t('menuSubscribers.stoppedInfo', { date: formatDateTime(detail.stopped_at), user: detail.stopped_by || '-' }) }}: {{ detail.stop_reason }}
              </div>
            </div>
          </div>

          <!-- deliveries -->
          <h5 class="msubs-section">{{ $t('menuSubscribers.deliveries') }}</h5>
          <el-table :data="detail.delivery_list" style="width: 100%" :empty-text="$t('menuSubscribers.noDeliveries')">
            <el-table-column :label="$t('menuSubscribers.date')" min-width="110">
              <template slot-scope="props">{{ formatDate(props.row.date) }}</template>
            </el-table-column>
            <el-table-column :label="$t('menuSubscribers.menu')" min-width="140">
              <template slot-scope="props">{{ props.row.menu_name || '-' }}</template>
            </el-table-column>
            <el-table-column :label="$t('menuSubscribers.deliveryStatus')" min-width="150">
              <template slot-scope="props">
                <span class="msubs-dstate" :class="'msubs-dstate--' + props.row.status">{{ $t('menuSubscribers.deliveryStatuses.' + props.row.status) }}</span>
                <div v-if="props.row.status === 'scheduled'" class="msubs-muted">{{ $t('menuSubscribers.orderAt', { date: formatDateTime(props.row.create_order_at) }) }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('menuSubscribers.order')" min-width="150">
              <template slot-scope="props">
                <template v-if="props.row.order">
                  #{{ props.row.order.number || '-' }} <span class="msubs-muted">(ID {{ props.row.order.id }})</span>
                  <div><span class="msubs-ostate">{{ props.row.order.status }}</span></div>
                </template>
                <span v-else class="msubs-muted">-</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('menuSubscribers.value')" min-width="90">
              <template slot-scope="props">{{ props.row.value }} {{ currency }}</template>
            </el-table-column>
            <el-table-column :label="$t('menuSubscribers.note')" min-width="180">
              <template slot-scope="props"><small :class="{ 'text-danger': props.row.status === 'error' }">{{ props.row.error_message }}</small></template>
            </el-table-column>
          </el-table>

          <!-- payments -->
          <h5 class="msubs-section">{{ $t('menuSubscribers.payments') }}</h5>
          <p v-if="!detail.payments.length" class="msubs-muted">{{ $t('menuSubscribers.noPayments') }}</p>
          <div v-for="payment in detail.payments" :key="'pay' + payment.id" class="msubs-payment">
            <div class="msubs-payment__row">
              <strong>{{ payment.reference }}</strong>
              <span>{{ payment.amount }} {{ currency }}</span>
              <span class="msubs-pstate" :class="'msubs-pstate--' + payment.status">{{ $t('menuSubscribers.paymentStatuses.' + payment.status) }}</span>
              <span class="msubs-muted">{{ formatDateTime(payment.paid_at || payment.created_at) }}</span>
              <span v-if="payment.gateway_transaction_id" class="msubs-muted">ep_id {{ payment.gateway_transaction_id }}</span>
              <span v-if="payment.refunded > 0" class="text-danger">{{ $t('menuSubscribers.refunded') }}: {{ money(payment.refunded) }}</span>
            </div>
            <div v-if="payment.error_message" class="msubs-muted msubs-small">{{ payment.error_message }}</div>
            <div v-for="refund in payment.refunds" :key="'ref' + refund.id" class="msubs-refund" :class="'msubs-refund--' + refund.status">
              <i class="fa fa-undo"></i>
              {{ refund.amount }} {{ currency }}
              · {{ $t('menuSubscribers.refundStatuses.' + refund.status) }}<span v-if="refund.type"> ({{ refund.type }})</span>
              · {{ formatDateTime(refund.created_at) }}<span v-if="refund.user"> · {{ refund.user }}</span>
              <div class="msubs-small">{{ refund.reason }}<span v-if="refund.error_message" class="text-danger"> — {{ refund.error_message }}</span></div>
            </div>
          </div>

          <!-- history -->
          <h5 class="msubs-section">{{ $t('menuSubscribers.history') }}</h5>
          <p v-if="!detail.events.length" class="msubs-muted">{{ $t('menuSubscribers.noHistory') }}</p>
          <div v-for="(event, index) in detail.events" :key="'ev' + index" class="msubs-event">
            <strong>{{ $t('menuSubscribers.events.' + event.type) }}</strong>
            <span v-if="event.type === 'refund' && event.data"> {{ money(event.data.amount) }} {{ currency }}</span>
            <span v-if="event.type === 'stop' && event.data"> · {{ $t('menuSubscribers.cancelledCount', { count: event.data.cancelled_deliveries }) }}</span>
            · {{ formatDateTime(event.created_at) }}<span v-if="event.user"> · {{ event.user }}</span>
            <div class="msubs-small">{{ event.comment }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {Table, TableColumn, Pagination, Select, Option} from 'element-ui'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'

  Vue.use(Table)
  Vue.use(TableColumn)
  Vue.use(Pagination)
  Vue.use(Select)
  Vue.use(Option)

  // must match MenuSubscriptionSubscriberController::STATUS_FILTERS (+ 'all')
  const CHIPS = ['all', 'paid', 'active', 'pending', 'abandoned', 'stopped', 'finished']

  export default {
    props: {
      route: {
        type: Object,
        default: () => ({})
      }
    },
    data () {
      const offerId = this.route.query && this.route.query.menu_subscription_id ? parseInt(this.route.query.menu_subscription_id, 10) : null
      return {
        chips: CHIPS,
        currency: '',
        offers: [],
        loading: false,
        items: [],
        counts: {},
        pagination: { total: 0, per_page: 20, current_page: 1, last_page: 1 },
        filters: {
          menu_subscription_id: offerId || null,
          status: 'all',
          search: '',
          page: 1
        },
        searchTimer: null,
        detail: null,
        detailLoading: false,
        box: null,
        busy: false,
        stopReason: '',
        refundAmount: '',
        refundReason: ''
      }
    },
    computed: {
      validRefund () {
        const amount = parseFloat(this.refundAmount)
        return !isNaN(amount) && amount > 0 && this.detail && amount <= parseFloat(this.detail.summary.refundable) + 0.001 && this.refundReason.trim() !== ''
      }
    },
    created () {
      this.currency = this.getCurrencySymbol(this.$session.get('selectedCompanyData').currency_iso)
      this.getOffers()
      this.reload()
    },
    beforeDestroy () {
      clearTimeout(this.searchTimer)
    },
    methods: {
      requestData () {
        return {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
      },
      getOffers () {
        this.apiRequest('/menu-subscription', 'GET', this.requestData(), (response) => {
          if (response.status === true) {
            this.offers = (response.data || []).map(offer => ({ id: offer.id, name: offer.name }))
          }
        })
      },
      reload () {
        this.filters.page = 1
        this.getItems()
      },
      getItems () {
        const data = { ...this.requestData(), status: this.filters.status, page: this.filters.page }
        if (this.filters.menu_subscription_id) {
          data.menu_subscription_id = this.filters.menu_subscription_id
        }
        if (this.filters.search.trim()) {
          data.search = this.filters.search.trim()
        }
        this.loading = true
        this.apiRequest('/menu-subscription/subscribers', 'GET', data, (response) => {
          this.loading = false
          if (response.status === true && response.data) {
            this.items = response.data.items || []
            this.counts = response.data.counts || {}
            this.pagination = response.data.pagination || this.pagination
          }
        })
      },
      setStatus (status) {
        this.filters.status = status
        this.reload()
      },
      onSearchInput () {
        clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => this.reload(), 400)
      },
      changePage (page) {
        this.filters.page = page
        this.getItems()
      },
      /* ---------------- Detail ---------------- */
      openDetail (row) {
        this.box = null
        this.detail = { ...row, summary: { deliveries: {}, refundable: 0 }, delivery_list: [], payments: [], events: [] }
        this.loadDetail(row.id)
      },
      loadDetail (id) {
        this.detailLoading = true
        this.apiRequest('/menu-subscription/subscribers/' + id, 'GET', this.requestData(), (response) => {
          this.detailLoading = false
          if (response.status === true && response.data) {
            this.detail = response.data
          } else {
            swal('', this.firstError(response), 'error').catch(() => {})
          }
        })
      },
      closeDetail () {
        this.detail = null
        this.box = null
        this.getItems()
      },
      toggleBox (box) {
        this.box = this.box === box ? null : box
        if (box === 'stop') {
          this.stopReason = ''
        }
        if (box === 'refund') {
          this.refundAmount = ''
          this.refundReason = ''
        }
      },
      stop () {
        swal({
          text: this.$t('menuSubscribers.stopQuestion', { name: this.detail.customer.name || ('#' + this.detail.customer.id) }),
          type: 'warning',
          showCancelButton: true
        }).then(() => {
          this.busy = true
          this.apiRequest('/menu-subscription/subscribers/' + this.detail.id + '/stop', 'POST', { ...this.requestData(), reason: this.stopReason }, (response) => {
            this.busy = false
            if (response.status === true && response.data) {
              this.detail = response.data
              this.box = null
              swal({ title: '', text: this.$t('menuSubscribers.stopped'), type: 'success', showConfirmButton: false, timer: 1500 }).catch(() => {})
            } else {
              swal('', this.firstError(response), 'error').catch(() => {})
            }
          })
        }).catch(() => {})
      },
      refund () {
        const amount = parseFloat(this.refundAmount).toFixed(2)
        swal({
          text: this.$t('menuSubscribers.refundQuestion', { amount: amount + ' ' + this.currency }),
          type: 'warning',
          showCancelButton: true
        }).then(() => {
          this.busy = true
          this.apiRequest('/menu-subscription/subscribers/' + this.detail.id + '/refund', 'POST', { ...this.requestData(), amount, reason: this.refundReason }, (response) => {
            this.busy = false
            // the detail comes back also on error (a part may have been refunded)
            if (response.data && response.data.id) {
              this.detail = response.data
            }
            if (response.status === true) {
              this.box = null
              swal({ title: '', text: this.$t('menuSubscribers.refundDone'), type: 'success', showConfirmButton: false, timer: 1500 }).catch(() => {})
            } else {
              swal('', this.firstError(response), 'error').catch(() => {})
            }
          })
        }).catch(() => {})
      },
      /* ---------------- Formatting ---------------- */
      money (value) {
        const number = parseFloat(value)
        return isNaN(number) ? '0.00' : number.toFixed(2)
      },
      formatDate (value) {
        if (!value) {
          return '-'
        }
        const parts = String(value).substr(0, 10).split('-')
        return parts.length === 3 ? parts[2] + '.' + parts[1] + '.' + parts[0] : value
      },
      formatDateTime (value) {
        if (!value) {
          return '-'
        }
        return this.formatDate(value) + ' ' + String(value).substr(11, 5)
      },
      addressText (address) {
        if (!address) {
          return '-'
        }
        if (address.formatted_address) {
          return address.formatted_address
        }
        return [address.street, address.number, address.city].filter(Boolean).join(' ')
      },
      firstError (response) {
        const errors = (response && response.errors) || {}
        const key = Object.keys(errors)[0]
        if (!key) {
          return this.$t('validations.wentWrong')
        }
        return Array.isArray(errors[key]) ? errors[key][0] : errors[key]
      },
      goBack () {
        this.$bus.$emit('refresh')
        this.$bus.$emit('close-modal')
      }
    }
  }
</script>
<style>
  .msubs-title {
    margin-top: 0;
  }
  .msubs-block {
    display: block;
  }
  .msubs-small {
    font-size: 12px;
  }
  .msubs-muted {
    color: #888;
    font-size: 12px;
  }
  .msubs-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 10px;
  }
  .msubs-filter .el-select {
    width: 240px;
  }
  .msubs-filter--search input {
    width: 280px;
  }
  .msubs-label {
    display: block;
    font-size: 12px;
    color: #777;
    margin-bottom: 4px;
  }
  .msubs-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }
  .msubs-chip {
    cursor: pointer;
    border: 1px solid #ddd;
    border-radius: 16px;
    padding: 4px 12px;
    font-size: 13px;
    color: #555;
  }
  .msubs-chip.is-active {
    background: #7e57c2;
    border-color: #7e57c2;
    color: #fff;
  }
  .msubs-chip__count {
    display: inline-block;
    margin-left: 4px;
    font-weight: 600;
  }
  .msubs-table .el-table__row {
    cursor: pointer;
  }
  .msubs-link {
    cursor: pointer;
  }
  .msubs-pagination {
    margin-top: 12px;
    text-align: center;
  }
  .msubs-state,
  .msubs-dstate,
  .msubs-pstate,
  .msubs-ostate {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 12px;
    background: #f5f5f5;
    color: #666;
    border: 1px solid #e0e0e0;
  }
  .msubs-state--active,
  .msubs-dstate--order_created,
  .msubs-pstate--success {
    background: #e8f5e9;
    color: #2e7d32;
    border-color: #c8e6c9;
  }
  .msubs-state--pending,
  .msubs-dstate--scheduled,
  .msubs-pstate--pending {
    background: #e3f2fd;
    color: #1565c0;
    border-color: #bbdefb;
  }
  .msubs-state--stopped,
  .msubs-dstate--error,
  .msubs-pstate--failed {
    background: #ffebee;
    color: #c62828;
    border-color: #ffcdd2;
  }
  .msubs-pstate--refunded {
    background: #fff3e0;
    color: #e65100;
    border-color: #ffcc80;
  }
  .msubs-detail__head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 10px;
  }
  .msubs-actions {
    display: flex;
    gap: 8px;
  }
  .msubs-box {
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 12px;
  }
  .msubs-box--stop {
    background: #ffebee;
    border: 1px solid #ffcdd2;
  }
  .msubs-box--refund {
    background: #fff8e1;
    border: 1px solid #ffe082;
  }
  .msubs-box__buttons {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
  .msubs-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 8px;
    margin: 6px 0 10px;
  }
  .msubs-card {
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    font-size: 12px;
    color: #777;
  }
  .msubs-card strong {
    font-size: 15px;
    color: #333;
  }
  .msubs-card--ok strong {
    color: #2e7d32;
  }
  .msubs-card--warn strong {
    color: #e65100;
  }
  .msubs-deliveries-line {
    font-size: 13px;
  }
  .msubs-info > div > div {
    margin-bottom: 3px;
    font-size: 13px;
  }
  .msubs-section {
    margin-top: 18px;
    font-weight: 600;
  }
  .msubs-payment {
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 8px 10px;
    margin-bottom: 8px;
  }
  .msubs-payment__row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    align-items: center;
  }
  .msubs-refund {
    margin: 6px 0 0 14px;
    font-size: 13px;
  }
  .msubs-refund--failed {
    color: #c62828;
  }
  .msubs-event {
    border-left: 3px solid #d1c4e9;
    padding: 4px 10px;
    margin-bottom: 6px;
    font-size: 13px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/MenuSubscriptions/MenuSubscribersPage.vue